<template>
  <section ref="root" class="homeLayout">
    <header class="homeLayout-header">
      <section class="homeLayout-header_actions">
        <v-btn
          icon
          :class="{
            ['homeLayout-header_actions--active']: displayType === 'icons',
          }"
          @click="handleDisplayType('icons')"
        >
          <span class="icon icon-tile" />
        </v-btn>
        <v-btn
          icon
          :class="{
            ['homeLayout-header_actions--active']: displayType === 'list',
          }"
          @click="handleDisplayType('list')"
        >
          <span class="icon icon-list" />
        </v-btn>
      </section>
      <v-btn
        small
        class="homeLayout-header_thumbnail"
        :class="{ ['homeLayout-header_thumbnail--active']: displayThumbnail }"
        @click="toggleDisplayThumbnail"
        >{{ displayThumbnail ? 'サムネイル非表示' : 'サムネイル表示' }}</v-btn
      >
    </header>
    <section v-if="layoutLoading" class="homeLayout-loading">
      <v-progress-circular
        :size="30"
        :width="2"
        indeterminate
      ></v-progress-circular>
    </section>
    <template v-else>
      <layout-icons
        v-if="displayType === 'icons'"
        ref="layoutIcons"
        :content-height="innerLayoutHeight"
        :layers="[...selectedLayers]"
        :cams="[...selectedCams]"
        :display-thumbnail="displayThumbnail"
        @change="handleChange"
        @mounted="handleResize"
      />
      <layout-list
        v-else
        ref="layoutList"
        :content-height="innerLayoutHeight"
        :layers="[...selectedLayers]"
        :cams="[...selectedCams]"
        :display-thumbnail="displayThumbnail"
        @change="handleChange"
        @mounted="handleResize"
      />
    </template>
    <footer class="homeLayout-footer">
      <section class="homeLayout-footer_actions">
        <v-btn class="homeLayout-footer_actions--cancel" @click="handleCancel"
          >キャンセル</v-btn
        >
        <v-btn
          :disabled="!canSave"
          class="homeLayout-footer_actions--save"
          @click="handleSave"
          >保存</v-btn
        >
      </section>
    </footer>
  </section>
</template>

<script>
import debounce from 'lodash/debounce';
import { mapActions, mapState } from 'vuex';
import LayoutIcons from './LayoutIcons.vue';
import LayoutList from './LayoutList.vue';

export default {
  name: 'HomeLayout',
  components: {
    LayoutIcons,
    LayoutList,
  },
  props: {
    dialogRef: {
      type: Object,
      default: () => null,
    },
  },
  data() {
    return {
      displayType: 'icons', // [icons, list]
      displayThumbnail: false,
      selectedCams: [],
      selectedLayers: [],
      innerLayoutHeight: 'auto',
      resizeObserver: null,
    };
  },
  computed: {
    ...mapState({
      cameraLayout: (state) => state.home.cameraLayout,
      layoutLoading: (state) => state.home.layoutLoading,
      cameraList: (state) => state.common.cameraList,
    }),
    layers() {
      return this.cameraLayout.map((layer) =>
        this.cameraList.find((camera) => layer.id === camera.id)
      );
    },
    cams() {
      return this.cameraList.filter((camera) => {
        return (
          this.cameraLayout.findIndex((layer) => {
            return layer.id === camera.id;
          }) === -1
        );
      });
    },
    canSave() {
      return (
        JSON.stringify(this.selectedLayers) !== JSON.stringify(this.layers)
      );
    },
  },
  watch: {
    cameraLayout: {
      handler() {
        this.selectedCams = [...this.cams];
        this.selectedLayers = [...this.layers];
      },
      deep: true,
    },
  },
  created() {
    this.selectedCams = [...this.cams];
    this.selectedLayers = [...this.layers];
    this.debounceResize = debounce(this.handleResize, 1);
  },
  mounted() {
    this.resizeObserver = new ResizeObserver(this.debounceResize).observe(
      this.dialogRef.$refs.content
    );
    this.init();
  },
  beforeDestroy() {
    if (this.resizeObserver) {
      this.resizeObserver.unobserve(this.dialogRef.$refs.content);
    }
  },
  methods: {
    ...mapActions('home', ['fetchCameraLayout']),
    async init() {
      if (this.cameraLayout.length === 0 && !this.layoutLoading) {
        await this.fetchCameraLayout();
      }
    },
    handleAddFakeItem() {
      this.selectedCams = [...this.selectedCams, { ...this.cams[0] }];
    },
    handleResize() {
      const contentRef = this.dialogRef.$refs.content;
      const layoutListRef = this.$refs.layoutList;
      const layoutIconsRef = this.$refs.layoutIcons;
      const contentHeight = contentRef.offsetHeight;
      let innerLayoutHeight = 0;
      let layoutCamsHeight = 0;
      if (layoutListRef) {
        innerLayoutHeight = layoutListRef.$el.offsetHeight;
        layoutCamsHeight = layoutListRef.$refs.layoutCams.offsetHeight;
      } else if (layoutIconsRef) {
        innerLayoutHeight = layoutIconsRef.$el.offsetHeight;
        layoutCamsHeight = layoutIconsRef.$refs.layoutCams.offsetHeight;
      }
      const marginTopBottom = 30;
      const headerSize = 150;
      const footerSize = 0;
      innerLayoutHeight = contentHeight - marginTopBottom * 2 - headerSize;
      if (layoutCamsHeight + footerSize > innerLayoutHeight) {
        this.innerLayoutHeight = `${innerLayoutHeight}px`;
      } else {
        this.innerLayoutHeight = 'auto';
      }
    },
    handleChange({ value, type }) {
      switch (type) {
        case 'cams':
          this.selectedCams = [...value];
          break;
        case 'layers':
          this.selectedLayers = [...value];
          break;
        default:
          break;
      }
    },
    handleDisplayType(type) {
      this.displayType = type;
    },
    toggleDisplayThumbnail() {
      this.displayThumbnail = !this.displayThumbnail;
      this.$nextTick(() => {
        this.handleResize();
      });
    },
    handleSave() {
      this.$emit('save');
    },
    handleCancel() {
      this.clear();
      this.$emit('cancel');
    },
    clear() {
      this.selectedCams = [...this.cams];
      this.selectedLayers = [...this.layers];
    },
  },
};
</script>

<style lang="scss">
.homeLayout {
  position: relative;
  &-loading {
    width: 100%;
    text-align: center;
    padding: 10px 0 100px 0;
  }
  &_flex-gap {
    display: flex;
    flex: 1;
  }
  &-header {
    display: flex;
    justify-content: space-between;
    padding: 10px 30px;
    align-items: center;
    &_actions {
      & > .v-btn.theme--light.v-btn.v-btn--icon {
        font-size: 16px;
        margin-right: 10px;
      }
      &--active {
        &.theme--light.v-btn.v-btn--icon {
          color: #0075c2;
          &::before {
            opacity: 0.08;
          }
        }
      }
    }
    &_thumbnail {
      &.theme--light.v-btn.v-btn--has-bg {
        height: 23px;
        border-radius: 5px;
        box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.16);
        background-color: #fff;
        font-size: 12px;
        font-weight: bold;
        font-stretch: normal;
        font-style: normal;
        line-height: 1.08;
        letter-spacing: normal;
        text-align: right;
        color: #0075c2;
      }
      &--active {
        &.theme--light.v-btn.v-btn--has-bg {
          background-color: #dbe2f3;
          box-shadow: none;
        }
      }
    }
  }

  &-footer {
    display: flex;
    justify-content: center;
    align-items: center;
    height: 76px;
    position: absolute;
    bottom: 0;
    width: 100%;
    z-index: 2;
    background-color: #fff;

    &_actions {
      &--cancel {
        &.theme--light.v-btn.v-btn--has-bg {
          width: 120px;
          height: 36px;
          border-radius: 5px;
          box-shadow: 0 2px 4px 0 rgba(0, 0, 0, 0.16);
          background-color: #fff;
          font-size: 14px;
          font-weight: normal;
          font-stretch: normal;
          font-style: normal;
          line-height: 1.5;
          letter-spacing: normal;
          text-align: center;
          color: #666;
        }
        margin-right: 30px;
      }
      &--save {
        &.theme--light.v-btn.v-btn--has-bg {
          width: 120px;
          height: 36px;
          margin: 0 0 0 2px;
          padding: 6px 5.4px 8px 6.4px;
          border-radius: 5px;
          box-shadow: 0 2px 4px 0 rgba(0, 0, 0, 0.16);
          background-color: #0075c2;
          font-size: 14px;
          font-weight: bold;
          font-stretch: normal;
          font-style: normal;
          letter-spacing: normal;
          text-align: center;
          color: #fff;
        }
        &.theme--light.v-btn.v-btn--disabled.v-btn--has-bg {
          background-color: #f3f3f3 !important;
          color: #ccc !important;
          box-shadow: none !important;
        }
      }
    }
  }
}
</style>
